// One place for every user-facing error. Each has a stable CODE (for logs / support / analytics)
// and a plain message (shown to the user). Add new ones here instead of writing ad-hoc strings.
export type AppErrorCode =
  | 'CALL_NOT_CONNECTED' | 'CALL_SELF' | 'CALL_BLOCKED' | 'CALL_BANNED' | 'CALL_BUSY'
  | 'CALL_NO_MIC' | 'CALL_NO_CAMERA' | 'CALL_NETWORK' | 'CALL_NO_ANSWER' | 'CALL_DECLINED' | 'CALL_FAILED'
  | 'AUTH_REQUIRED' | 'PROFILE_NOT_FOUND' | 'PROFILE_PRIVATE' | 'RATE_LIMITED' | 'PERMISSION_DENIED' | 'UNKNOWN'

export const ERRORS: Record<AppErrorCode, (who?: string) => string> = {
  CALL_NOT_CONNECTED: (who) => `You are not connected with ${who ? '@' + who : 'this user'}. Connect with them first, then you can call.`,
  CALL_SELF: () => 'You cannot call yourself.',
  CALL_BLOCKED: (who) => `You cannot call ${who ? '@' + who : 'this user'} right now.`,
  CALL_BANNED: () => 'Your account is restricted, so calls are disabled.',
  CALL_BUSY: () => 'You are already in a call.',
  CALL_NO_MIC: () => 'Microphone access is blocked. Allow the microphone in your phone/browser settings and try again.',
  CALL_NO_CAMERA: () => 'Camera access is blocked. Allow the camera in your phone/browser settings and try again.',
  CALL_NETWORK: () => 'Network problem. Check your internet and try again.',
  CALL_NO_ANSWER: (who) => `${who ? '@' + who : 'The user'} did not answer.`,
  CALL_DECLINED: (who) => `${who ? '@' + who : 'The user'} declined the call.`,
  CALL_FAILED: () => 'Could not start the call. Please try again.',
  AUTH_REQUIRED: () => 'Sign up or log in to do this.',
  PROFILE_NOT_FOUND: () => 'This profile does not exist.',
  PROFILE_PRIVATE: () => 'This account is private.',
  RATE_LIMITED: () => 'Too many attempts. Please wait a minute and try again.',
  PERMISSION_DENIED: () => 'You do not have permission to do this.',
  UNKNOWN: () => 'Something went wrong. Please try again.',
}

export const errorMessage = (code: AppErrorCode, who?: string) => `${ERRORS[code](who)}`
export const errorWithCode = (code: AppErrorCode, who?: string) => `${ERRORS[code](who)} (${code})`

// ---------------------------------------------------------------------------
// Clean user-facing text. Nothing technical (error codes, "permission-denied", paths, stack text,
// "FirebaseError: ...") may ever reach a toast / dialog / form. Use friendlyError() in every catch
// block and cleanToast() on any ready-made string; both fall back to a plain sentence.
// ---------------------------------------------------------------------------
const GENERIC = 'Something went wrong. Please try again.'
const TECHY = /(permission-denied|unauthenticated|failed-precondition|invalid-argument|resource-exhausted|deadline-exceeded|db-http|firestore|firebase|\bsql\b|\bd1\b|TypeError|ReferenceError|SyntaxError|RangeError|\bundefined\b|\bNaN\b|\[object|\bError:|Exception|stack trace|\bat [\w.$]+ \(|https?:\/\/|\/api\/|\.(tsx?|jsx?|mjs)\b|No document to update|Missing or insufficient|too-many-writes|document-too-large|invalid-data|invalid-guard|network-error|\bauth\/[a-z-]+|\bcode\s*[:=]|\bstatus\s*[:=]\s*\d{3}|^[{\[<])/i
const CODE_TOKEN = /^[a-z0-9]+([-_][a-z0-9]+)+$/i // "not-found", "username_taken", "CALL_FAILED"
const SHOUT_TOKEN = /^[A-Z][A-Z0-9_]{2,}$/

/** True when the text looks like code / a raw error rather than something a person should read. */
export const looksTechnical = (text: unknown): boolean => {
  const t = String(text ?? '').trim()
  if (!t) return true
  if (CODE_TOKEN.test(t) || SHOUT_TOKEN.test(t)) return true
  return TECHY.test(t)
}

/** Strips trailing "(some-code)" / "(AUTH_REQUIRED)" tags and swaps raw technical text for a plain sentence. */
export const cleanToast = (text: unknown, fallback: string = GENERIC): string => {
  let t = String(text ?? '').trim()
  t = t.replace(/\s*\((?:[a-z]+\/)?[a-z0-9]+(?:[-_][a-z0-9]+)+\)/gi, '').replace(/\s*\([A-Z][A-Z0-9_]{3,}\)/g, '').trim()
  return t && !looksTechnical(t) ? t : fallback
}

const CODE_MESSAGES: Record<string, string> = {
  'permission-denied': 'You do not have permission to do this.',
  unauthenticated: 'Please log in again to continue.',
  'not-found': 'This item no longer exists.',
  unavailable: 'Network problem. Check your internet and try again.',
  timeout: 'This is taking too long. Check your internet and try again.',
  aborted: 'That did not go through. Please try again.',
  'resource-exhausted': 'Too many attempts. Please wait a minute and try again.',
  'deadline-exceeded': 'This is taking too long. Check your internet and try again.',
}

/** Turn ANY caught error into a short, plain sentence for the user. */
export const friendlyError = (err: unknown, fallback: string = GENERIC): string => {
  const e = err as { code?: unknown; message?: unknown } | null | undefined
  const msg = typeof e?.message === 'string' ? e.message : typeof err === 'string' ? err : ''
  const cleaned = cleanToast(msg, '')
  if (cleaned) return cleaned
  const code = String(e?.code || '').replace(/^firestore\//, '').replace(/^functions\//, '')
  if (CODE_MESSAGES[code]) return CODE_MESSAGES[code]
  if (/offline|network|failed to fetch|load failed/i.test(msg)) return CODE_MESSAGES.unavailable
  return fallback
}
